import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import {
  useAddGalleryMutation,
  useUpdateGalleryMutation,
  useGetCategoriesQuery,
} from "../api/galleriesApi";
import { Gallery } from "../types/gallery";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  gallery?: Gallery | null;
}

const AddEditGallery: React.FC<Props> = ({
  open,
  setModalOpen,
  onSuccess,
  gallery,
}) => {
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    category: string;
    is_featured: boolean;
    images: File[];
    existingImages?: string[];
  }>({
    title: "",
    description: "",
    category: "",
    is_featured: false,
    images: [],
    existingImages: [],
  });

  const [addGallery] = useAddGalleryMutation();
  const [updateGallery] = useUpdateGalleryMutation();
  const { data: categoriesData, isLoading: loadingCategories, isError: categoriesError } =
    useGetCategoriesQuery();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (gallery) {
      setFormData({
        title: gallery.title || "",
        description: gallery.description || "",
        category: gallery.category?.id?.toString() || "",
        is_featured: gallery.is_featured || false,
        images: [],
        existingImages: gallery.images || [],
      });
    } else {
      setFormData({
        title: "",
        description: "",
        category: "",
        is_featured: false,
        images: [],
        existingImages: [],
      });
    }
  }, [gallery]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (files: FileList | null) => {
    if (files) {
      handleInputChange("images", Array.from(files));
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!formData.title.trim()) {
      setSubmissionError("Title is required");
      return;
    }

    setIsSaving(true);
    setSubmissionError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("category_id", formData.category);
      formDataToSend.append(
        "is_featured",
        formData.is_featured ? "1" : "0"
      );

      // append all images as images[]
      formData.images.forEach((file) => {
        formDataToSend.append("images[]", file);
      });

      if (gallery) {
        await updateGallery({
          id: gallery.id.toString(),
          body: formDataToSend,
        }).unwrap();
      } else {
        await addGallery({
          body: formDataToSend,
        }).unwrap();
      }

      setModalOpen(false);
      onSuccess?.();
    } catch (error: any) {
      setSubmissionError(
        error?.data?.message ||
          "An error occurred while saving the gallery"
      );
    } finally {
      setIsSaving(false);
    }
  }, [formData, gallery, addGallery, updateGallery, setModalOpen, onSuccess]);

  const handleClose = () => {
    setModalOpen(false);
    setSubmissionError(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{gallery ? "Edit Gallery" : "Add New Gallery"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {submissionError && <Alert severity="error">{submissionError}</Alert>}
          {categoriesError && <Alert severity="error">Failed to load categories</Alert>}

          <TextField
            label="Title"
            value={formData.title}
            onChange={(e) => handleInputChange("title", e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            fullWidth
            multiline
            rows={3}
          />

          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.category}
              onChange={(e) =>
                handleInputChange("category", e.target.value)
              }
              label="Category"
              disabled={loadingCategories}
            >
              {loadingCategories && (
                <MenuItem disabled value="">
                  Loading categories...
                </MenuItem>
              )}
              {!loadingCategories && (categoriesData?.data?.length)
                ? categoriesData.data.map((category: any) => (
                    <MenuItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </MenuItem>
                  ))
                : !loadingCategories && (
                    <MenuItem disabled value="">
                      No categories available
                    </MenuItem>
                  )}
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <input
              type="checkbox"
              id="is_featured"
              checked={formData.is_featured}
              onChange={(e) =>
                handleInputChange("is_featured", e.target.checked)
              }
            />
            <label htmlFor="is_featured">Featured Image</label>
          </Box>

          <Box>
            <label htmlFor="images">Images</label>
            <input
              type="file"
              id="images"
              accept="image/*"
              multiple
              onChange={(e) => handleFileChange(e.target.files)}
              style={{ marginTop: "8px" }}
            />
            {formData.existingImages &&
              formData.existingImages.length > 0 &&
              formData.images.length === 0 && (
                <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {formData.existingImages.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`Current image ${idx}`}
                      style={{
                        width: 100,
                        height: 100,
                        objectFit: "cover",
                      }}
                    />
                  ))}
                </Box>
              )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {isSaving ? "Saving..." : gallery ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditGallery;
